import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { fetchSettings, saveSettings, type Settings as TSettings } from '@/lib/api'
import { Card } from '@/components/Surface'
import { SubPage } from '@/components/SubPage'
import { Segment, Switch } from '@/components/Form'
import { getThemeMode, setThemeMode, type ThemeMode } from '@/lib/tg'

export function Settings() {
  const qc = useQueryClient()
  const [theme, setTheme] = useState<ThemeMode>(getThemeMode())
  const { data } = useQuery({ queryKey: ['settings'], queryFn: fetchSettings })
  const save = useMutation({
    mutationFn: saveSettings,
    onMutate: async patch => {
      await qc.cancelQueries({ queryKey: ['settings'] })
      const prev = qc.getQueryData<TSettings>(['settings'])
      if (prev) qc.setQueryData<TSettings>(['settings'], { ...prev, ...patch })
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['settings'], ctx.prev)
    },
  })

  if (!data) {
    return <SubPage title="настройки"><div className="px-5 text-[var(--color-muted)]">…</div></SubPage>
  }

  return (
    <SubPage title="настройки" subtitle="тема, язык и уведомления">
      <div className="px-5 space-y-5 stagger">
        {/* Theme */}
        <div style={{ ['--i' as never]: 0 }}>
          <div className="text-[13px] text-[var(--color-muted)] mb-2 px-1">тема</div>
          <Segment
            value={theme}
            onChange={(v: ThemeMode) => { setTheme(v); setThemeMode(v) }}
            options={[
              { value: 'auto', label: 'авто' },
              { value: 'light', label: 'светлая' },
              { value: 'dark', label: 'тёмная' },
            ]}
          />
        </div>

        {/* Language */}
        <div style={{ ['--i' as never]: 1 }}>
          <div className="text-[13px] text-[var(--color-muted)] mb-2 px-1">язык</div>
          <Segment
            value={data.language}
            onChange={v => save.mutate({ language: v })}
            options={[
              { value: 'ru', label: 'русский' },
              { value: 'en', label: 'english' },
            ]}
          />
        </div>

        {/* Notifications */}
        <div style={{ ['--i' as never]: 2 }}>
          <div className="text-[13px] text-[var(--color-muted)] mb-2 px-1">уведомления</div>
          <Card className="overflow-hidden">
            <ToggleRow
              title="за 3 дня до окончания"
              hint="напомним продлить заранее"
              checked={data.notify_3d}
              onChange={v => save.mutate({ notify_3d: v })}
              divider
            />
            <ToggleRow
              title="за 1 день"
              hint="последний шанс перед отключением"
              checked={data.notify_1d}
              onChange={v => save.mutate({ notify_1d: v })}
              divider
            />
            <ToggleRow
              title="трафик заканчивается"
              hint="когда осталось меньше 10%"
              checked={data.notify_low_traffic}
              onChange={v => save.mutate({ notify_low_traffic: v })}
            />
          </Card>
        </div>

        <div
          style={{ ['--i' as never]: 3 }}
          className="text-[12px] text-[var(--color-muted)] leading-relaxed px-1"
        >
          Уведомления приходят в этот же чат от @CicxdaVPN_bot. Бот никогда не пишет
          ничего, кроме того, что ты разрешил.
        </div>
      </div>
    </SubPage>
  )
}

function ToggleRow({
  title,
  hint,
  checked,
  onChange,
  divider,
}: {
  title: string
  hint?: string
  checked: boolean
  onChange: (v: boolean) => void
  divider?: boolean
}) {
  return (
    <div
      className="px-5 py-4 flex items-center gap-4"
      style={{ borderBottom: divider ? '1px solid var(--color-line)' : 'none' }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[14.5px] font-semibold">{title}</div>
        {hint && <div className="text-[12px] text-[var(--color-muted)] mt-0.5">{hint}</div>}
      </div>
      <Switch checked={checked} onChange={onChange} />
    </div>
  )
}
